import type { ClusterSize } from '@/composables/useMapGeometry';
import type { PropertiesListingMapState } from '~/composables/useMapListing';
import type { Cluster } from '~/core/entities/api/ApiClusters';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { GeocodeResult } from '~/utils/geocoding';

import { calculateClusterSizes } from '@/composables/useMapGeometry';
import { usePropertySearchEventKey } from '~/composables/propertySearch/context';
import { useMapSearchMode } from '~/composables/propertySearch/mapSearchMode';
import { useGoogleMaps } from '~/composables/useGoogleMaps';
import { EventBusKeys } from '~/core/helpers/symbols';
import { createSafeDebouncedFunction } from '~/utils';
import {
	buildLocationAddress,
	geocodeAddress,
	getMacroregionAddress
} from '~/utils/geocoding';
import { getMacroregionMapViewport } from '~/utils/macroregionMapViewport';

const MAP_ZOOM_CONSTRAINTS = {
	DEFAULT: 13,
	MIN_AFTER_BOUNDS: 12,
	FIT_BOUNDS_PADDING: 7
} as const;

const DEBOUNCE_DELAYS = {
	URL_UPDATE: 300,
	MAP_BOUNDS: 0,
	MAX_WAIT: 800
} as const;

const IDLE_TIMEOUT_MS = 5000;

const DEFAULT_MAP_ZOOM = 15;

interface MapControllerConfig {
	filters: Ref<PropertyFilters>;
	gmapRef: Ref<{
		mapInstance?: google.maps.Map;
		fitBoundsToMarkers?: (
			positions: { lat: number; lng: number }[],
			padding?: number
		) => void;
	} | null>;
	clusters: Ref<Cluster[]>;
	selectedClustersData: Ref<ReadonlySet<string>>;
	normalizeClusterCoordinates: (coordinates: [number, number]) => {
		lat: number;
		lng: number;
	};
	originalUpdateClusters: (filtersData: PropertyFilters) => Promise<void>;
	pageConfig: {
		page: 'imoveis' | 'colecoes';
		collection?: string;
		wishlistId?: string;
	};
}

export enum MapState {
	INITIALIZING = 'initializing',
	READY = 'ready',
	UPDATING_LOCATION = 'updating-location',
	UPDATING_SEE_ALL_CITY = 'updating-see-all-city',
	UPDATING_FROM_INTERACTION = 'updating-from-interaction',
	APPLYING_FIT_BOUNDS = 'applying-fit-bounds',
	NAVIGATING_BY_CLUSTER = 'navigating-by-cluster'
}

interface MapControllerState {
	currentState: MapState;
	currentZoom: number;
	pendingOperations: {
		fitBoundsCallback: (() => void) | null;
		clustersUpdate: PropertyFilters | null;
	};
	locationChangeOrigin: 'filters-bar' | 'initialization' | null;
	isProgrammaticOperation: boolean;
}

interface LocationChangeData {
	lat?: number;
	lng?: number;
	regions?: string[];
	cities?: string[];
	states?: string[];
	macroregions?: string[];
}

function getArrayLength(arr: any[] | undefined): number {
	return arr?.length || 0;
}

function hasArrayAdditions(
	newArr: any[] | undefined,
	oldArr: any[] | undefined
): boolean {
	return getArrayLength(newArr) > getArrayLength(oldArr);
}

function hasArrayRemovals(
	newArr: any[] | undefined,
	oldArr: any[] | undefined
): boolean {
	return getArrayLength(newArr) < getArrayLength(oldArr);
}

function hasFirstItemChanged(
	newArr: any[] | undefined,
	oldArr: any[] | undefined
): boolean {
	return newArr?.[0] !== oldArr?.[0] && !!newArr?.[0];
}

function hasLocationFieldsAdditions(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		hasArrayAdditions(newFilters.regions, currentFilters.regions) ||
		hasArrayAdditions(newFilters.cities, currentFilters.cities) ||
		hasArrayAdditions(newFilters.macroregions, currentFilters.macroregions) ||
		(!currentFilters.addressSlug && !!newFilters.addressSlug) ||
		(!currentFilters.poiLocationLat && !!newFilters.poiLocationLat)
	);
}

function hasLocationFieldsSubstitutions(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		hasFirstItemChanged(newFilters.regions, currentFilters.regions) ||
		hasFirstItemChanged(newFilters.cities, currentFilters.cities) ||
		hasFirstItemChanged(newFilters.states, currentFilters.states)
	);
}

function hasLocationFieldsRemovals(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		hasArrayRemovals(newFilters.regions, currentFilters.regions) ||
		hasArrayRemovals(newFilters.cities, currentFilters.cities) ||
		hasArrayRemovals(newFilters.macroregions, currentFilters.macroregions) ||
		(!!currentFilters.addressSlug && !newFilters.addressSlug) ||
		(!!currentFilters.poiLocationLat && !newFilters.poiLocationLat)
	);
}

function hasMacroregionSubstitution(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		newFilters.macroregions?.[0] !== currentFilters.macroregions?.[0] &&
		!!newFilters.macroregions?.[0] &&
		!!currentFilters.macroregions?.[0]
	);
}

function hasPoiLocationAddition(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return !!newFilters.poiLocationLat && !currentFilters.poiLocationLat;
}

function requiresGeocodingForLocationChange(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	const hasAdditions = hasLocationFieldsAdditions(newFilters, currentFilters);
	const hasSubstitutions = hasLocationFieldsSubstitutions(
		newFilters,
		currentFilters
	);
	const hasMacroregionChange = hasMacroregionSubstitution(
		newFilters,
		currentFilters
	);
	const hasPoiAddition = hasPoiLocationAddition(newFilters, currentFilters);
	const hasRemovals = hasLocationFieldsRemovals(newFilters, currentFilters);

	if (
		hasRemovals &&
		!hasAdditions &&
		!hasSubstitutions &&
		!hasMacroregionChange &&
		!hasPoiAddition
	) {
		return false;
	}

	return (
		hasAdditions || hasSubstitutions || hasMacroregionChange || hasPoiAddition
	);
}

function hasLocationFiltersChanged(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return (
		requiresGeocodingForLocationChange(newFilters, currentFilters) ||
		hasLocationFieldsRemovals(newFilters, currentFilters)
	);
}

function hasSignificantLocationChange(
	newData: LocationChangeData,
	oldData: LocationChangeData
): boolean {
	const newFilters = {
		regions: newData.regions,
		cities: newData.cities,
		states: newData.states,
		macroregions: newData.macroregions,
		poiLocationLat: newData.lat,
		addressSlug: undefined
	} as PropertyFilters;

	const oldFilters = {
		regions: oldData.regions,
		cities: oldData.cities,
		states: oldData.states,
		macroregions: oldData.macroregions,
		poiLocationLat: oldData.lat,
		addressSlug: undefined
	} as PropertyFilters;

	return requiresGeocodingForLocationChange(newFilters, oldFilters);
}

function shouldApplyFitBoundsForLocation(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): boolean {
	return requiresGeocodingForLocationChange(newFilters, currentFilters);
}

export function useMapController(config: MapControllerConfig) {
	const mapMode = useMapSearchMode();
	const camera = mapMode.createCamera();
	const {
		filters,
		gmapRef,
		clusters,
		selectedClustersData,
		normalizeClusterCoordinates,
		originalUpdateClusters
	} = config;

	const { init: initGoogleMaps } = useGoogleMaps();
	const mapState = useState<Pick<PropertiesListingMapState, 'mapConfig'>>(
		mapMode.cameraStateKey,
		mapMode.initialCameraState
	);

	const state = reactive<MapControllerState>({
		currentState: MapState.INITIALIZING,
		currentZoom: mapState.value.mapConfig.zoom || DEFAULT_MAP_ZOOM,
		pendingOperations: {
			fitBoundsCallback: null,
			clustersUpdate: null
		},
		locationChangeOrigin: null,
		isProgrammaticOperation: false
	});

	let locationRevision = 0;
	let pendingLocationFilters: PropertyFilters | undefined;
	let preserveCameraOnClusterUpdate = false;
	let disposed = false;

	function markProgrammaticCamera() {
		camera.markProgrammatic();
		state.isProgrammaticOperation = true;
	}
	function beginCameraGesture() {
		camera.beginGesture(() => {
			state.isProgrammaticOperation = false;
		});
	}
	const clusterSizes = ref<Map<string, ClusterSize>>(new Map());

	const isInitializing = computed(
		() => state.currentState === MapState.INITIALIZING
	);
	const isReady = computed(() => state.currentState === MapState.READY);
	const isUpdatingLocation = computed(
		() => state.currentState === MapState.UPDATING_LOCATION
	);
	const isUpdatingSeeAllCity = computed(
		() => state.currentState === MapState.UPDATING_SEE_ALL_CITY
	);
	const isUpdatingFromInteraction = computed(
		() => state.currentState === MapState.UPDATING_FROM_INTERACTION
	);
	const isApplyingFitBounds = computed(
		() => state.currentState === MapState.APPLYING_FIT_BOUNDS
	);
	const isNavigatingByCluster = computed(
		() => state.currentState === MapState.NAVIGATING_BY_CLUSTER
	);

	function transitionTo(newState: MapState): void {
		state.currentState = newState;
	}

	function canUpdateClusters(): boolean {
		return state.currentState !== MapState.UPDATING_LOCATION;
	}

	function getRegionalViewport(locationFilters: PropertyFilters) {
		if (config.pageConfig.page !== 'imoveis' || config.pageConfig.wishlistId)
			return undefined;
		return getMacroregionMapViewport(locationFilters);
	}

	function applyRegionalViewport(locationFilters: PropertyFilters): boolean {
		const viewport = getRegionalViewport(locationFilters);
		const map = gmapRef.value?.mapInstance;
		if (!viewport || !map) return false;

		preserveCameraOnClusterUpdate = true;
		markProgrammaticCamera();
		mapState.value.mapConfig.bounds = undefined;
		mapState.value.mapConfig.center = { ...viewport.center };
		mapState.value.mapConfig.zoom = viewport.zoom;
		state.currentZoom = viewport.zoom;
		map.setCenter(viewport.center);
		map.setZoom(viewport.zoom);
		return true;
	}

	async function loadClusters(filtersData: PropertyFilters): Promise<void> {
		const revision = locationRevision;
		const viewport = getRegionalViewport(filtersData);
		// Zoom controls cluster resolution only; do not restrict the listing to
		// the initial viewport or publish programmatic bounds to the URL.
		const requestFilters =
			viewport && !filtersData.zoom
				? { ...filtersData, zoom: viewport.zoom }
				: filtersData;
		try {
			await originalUpdateClusters(requestFilters);
		} finally {
			if (
				!disposed &&
				revision === locationRevision &&
				isUpdatingLocation.value &&
				preserveCameraOnClusterUpdate
			) {
				finishLocationProcessing();
			}
		}
	}

	async function applyGeocodeResultToMap(
		result: GeocodeResult,
		shouldFitBounds: boolean = true
	): Promise<void> {
		if (!gmapRef.value?.mapInstance) return;
		const revision = locationRevision;

		markProgrammaticCamera();

		const center = result.bounds ? result.bounds.getCenter() : result.center;
		if (!center) return;

		return new Promise(resolve => {
			let idleListener: google.maps.MapsEventListener | null = null;

			const timeoutId = setTimeout(() => {
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}
				resolve();
			}, IDLE_TIMEOUT_MS);

			try {
				if (!gmapRef.value?.mapInstance) {
					clearTimeout(timeoutId);
					resolve();
					return;
				}

				idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
					clearTimeout(timeoutId);
					if (idleListener) {
						google.maps.event.removeListener(idleListener);
						idleListener = null;
					}

					if (
						!gmapRef.value?.mapInstance ||
						disposed ||
						revision !== locationRevision
					) {
						resolve();
						return;
					}

					mapState.value.mapConfig.center = center;

					if (result.bounds && shouldFitBounds) {
						mapState.value.mapConfig.bounds = result.bounds;
						const zoom = gmapRef.value.mapInstance.getZoom();
						if (zoom && zoom < MAP_ZOOM_CONSTRAINTS.MIN_AFTER_BOUNDS) {
							gmapRef.value.mapInstance.setZoom(
								MAP_ZOOM_CONSTRAINTS.MIN_AFTER_BOUNDS
							);
						}
					} else if (!shouldFitBounds) {
						mapState.value.mapConfig.bounds = undefined;
						gmapRef.value.mapInstance.setZoom(15);
					}

					resolve();
				});

				gmapRef.value.mapInstance.setCenter(center);
			} catch (error) {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
				}
				console.error('Error in applyGeocodeResultToMap:', error);
				resolve();
			}
		});
	}

	async function fitMapFromMacroregion(
		macroregion: string,
		shouldFitBounds: boolean = true
	): Promise<void> {
		const address = getMacroregionAddress(macroregion);
		if (!address) return;

		const revision = locationRevision;
		const result = await geocodeAddress(address);
		if (disposed || revision !== locationRevision) return;
		await applyGeocodeResultToMap(result, shouldFitBounds);
	}

	async function fitMapFromLocation(
		region: string,
		city: string,
		state: string,
		shouldFitBounds: boolean = true
	): Promise<void> {
		const address = buildLocationAddress(region, city, state);
		const revision = locationRevision;
		const result = await geocodeAddress(address);
		if (disposed || revision !== locationRevision) return;

		if (result.center || result.bounds) {
			await applyGeocodeResultToMap(result, shouldFitBounds);
		}
	}

	function fitMapFromPoi(lat: number, lng: number): Promise<void> {
		if (!gmapRef.value?.mapInstance) return Promise.resolve();
		const revision = locationRevision;

		markProgrammaticCamera();

		return new Promise(resolve => {
			let idleListener: google.maps.MapsEventListener | null = null;

			const timeoutId = setTimeout(() => {
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}
				resolve();
			}, IDLE_TIMEOUT_MS);

			try {
				if (!gmapRef.value?.mapInstance) {
					clearTimeout(timeoutId);
					resolve();
					return;
				}

				idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
					clearTimeout(timeoutId);
					if (idleListener) {
						google.maps.event.removeListener(idleListener);
						idleListener = null;
					}

					if (disposed || revision !== locationRevision) {
						resolve();
						return;
					}
					mapState.value.mapConfig.center = { lat, lng };
					const currentZoom = gmapRef.value?.mapInstance?.getZoom();
					if (!currentZoom || currentZoom < 15) {
						gmapRef.value!.mapInstance!.setZoom(15);
					}
					resolve();
				});

				gmapRef.value.mapInstance.setCenter({ lat, lng });
			} catch (error) {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
				}
				console.error('Error in fitMapFromPoi:', error);
				resolve();
			}
		});
	}

	function applyUrlBoundsToMap(locationFilters = filters.value): boolean {
		if (
			!gmapRef.value?.mapInstance ||
			!locationFilters.zoom ||
			!locationFilters.bounds
		) {
			return false;
		}

		const boundsArray = locationFilters.bounds.split(',').map(Number);
		if (
			boundsArray.length === 4 &&
			boundsArray.every(Number.isFinite) &&
			Number.isFinite(locationFilters.zoom)
		) {
			const [west, south, east, north] = boundsArray;

			const centerLat = (south! + north!) / 2;
			const centerLng = (west! + east!) / 2;

			preserveCameraOnClusterUpdate = true;
			markProgrammaticCamera();
			mapState.value.mapConfig.bounds = undefined;
			mapState.value.mapConfig.center = { lat: centerLat, lng: centerLng };
			mapState.value.mapConfig.zoom = locationFilters.zoom;
			state.currentZoom = locationFilters.zoom;
			gmapRef.value.mapInstance.setZoom(locationFilters.zoom);
			gmapRef.value.mapInstance.setCenter({ lat: centerLat, lng: centerLng });
			return true;
		}
		return false;
	}

	function fitBoundsToMarkers(): void {
		markProgrammaticCamera();

		if (clusters.value.length > 0 && gmapRef.value?.fitBoundsToMarkers) {
			const positions = clusters.value.map(cluster =>
				normalizeClusterCoordinates(cluster.geometry.coordinates)
			);

			gmapRef.value.fitBoundsToMarkers(
				positions,
				MAP_ZOOM_CONSTRAINTS.FIT_BOUNDS_PADDING
			);
		}

		nextTick(() => {
			transitionTo(MapState.READY);
			state.pendingOperations.fitBoundsCallback?.();
			state.pendingOperations.fitBoundsCallback = null;
		});
	}

	function updateClusterSizes(): void {
		if (!gmapRef.value?.mapInstance) return;

		const currentZoomValue =
			gmapRef.value.mapInstance.getZoom() ?? MAP_ZOOM_CONSTRAINTS.DEFAULT;
		clusterSizes.value = calculateClusterSizes(
			clusters.value,
			currentZoomValue
		);
	}

	async function initializeMapFromCurrentFilters(
		shouldFitBounds: boolean = true,
		locationFilters = filters.value
	): Promise<void> {
		if (import.meta.server) return;
		if (applyRegionalViewport(locationFilters)) return;
		const revision = locationRevision;

		// Ensure Google Maps API is loaded before geocoding operations
		await initGoogleMaps();
		if (disposed || revision !== locationRevision) return;
		// A slug-only navigation may have been resolved by the listing API while
		// clusters loaded. Keep that enrichment only for the same destination.
		if (
			locationFilters.locationSlug &&
			locationFilters.locationSlug === filters.value.locationSlug
		) {
			locationFilters = { ...filters.value, ...locationFilters };
		}

		const {
			poiLocationLat,
			poiLocationLng,
			locationSlug,
			regions,
			cities,
			states,
			macroregions
		} = locationFilters;

		const poi = mapMode.toPoi(poiLocationLat, poiLocationLng);
		if (poi) {
			await fitMapFromPoi(poi.lat, poi.lng);
			return;
		}

		const MACROREGION_SLUGS: Record<string, string> = {
			'sao-paulo-sp-brasil': 'São Paulo',
			'curitiba-pr-brasil': 'Curitiba'
		};

		if (locationSlug && MACROREGION_SLUGS[locationSlug]) {
			await fitMapFromMacroregion(
				MACROREGION_SLUGS[locationSlug],
				shouldFitBounds
			);
			return;
		}

		if (regions?.[0] || cities?.[0]) {
			await fitMapFromLocation(
				regions?.[0] || '',
				cities?.[0] || '',
				states?.[0] || '',
				shouldFitBounds
			);
			return;
		}

		if (macroregions?.[0]) {
			await fitMapFromMacroregion(macroregions[0], shouldFitBounds);
		}
	}

	function processLocationFiltersChange(
		origin: 'filters-bar' | 'initialization' = 'initialization',
		locationFilters = filters.value
	): void {
		locationRevision++;
		pendingLocationFilters = locationFilters;
		preserveCameraOnClusterUpdate = false;
		state.locationChangeOrigin = origin;

		transitionTo(MapState.UPDATING_LOCATION);
		if (!applyUrlBoundsToMap(locationFilters)) {
			applyRegionalViewport(locationFilters);
		}
	}

	function processSeeAllCity(
		origin: 'filters-bar' | 'initialization' = 'initialization'
	): void {
		state.locationChangeOrigin = origin;

		transitionTo(MapState.UPDATING_SEE_ALL_CITY);
	}

	function finishLocationProcessing(): void {
		if (state.currentState === MapState.READY) {
			return;
		}

		state.locationChangeOrigin = null;
		pendingLocationFilters = undefined;
		state.isProgrammaticOperation = false;
		transitionTo(MapState.READY);

		nextTick(() => {
			state.pendingOperations.fitBoundsCallback?.();
			state.pendingOperations.fitBoundsCallback = null;
		});
	}

	async function handleRegionChanged(shouldFitBounds = false): Promise<void> {
		if (isInitializing.value) return;

		if (shouldFitBounds) {
			markProgrammaticCamera();
			transitionTo(MapState.APPLYING_FIT_BOUNDS);
			processLocationFiltersChange('filters-bar');
		}
	}

	function handleDragEnd(): void {
		if (
			!gmapRef.value?.mapInstance ||
			!isReady.value ||
			state.isProgrammaticOperation
		) {
			return;
		}

		transitionTo(MapState.UPDATING_FROM_INTERACTION);

		let idleListener: google.maps.MapsEventListener | null = null;

		const timeoutId = setTimeout(() => {
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
				idleListener = null;
			}
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}, IDLE_TIMEOUT_MS);

		try {
			if (!gmapRef.value?.mapInstance) {
				clearTimeout(timeoutId);
				nextTick(() => {
					transitionTo(MapState.READY);
				});
				return;
			}

			idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}

				const bounds = gmapRef.value?.mapInstance?.getBounds();
				if (!bounds) {
					nextTick(() => {
						transitionTo(MapState.READY);
					});
					return;
				}

				debouncedUpdateMapBounds(bounds, undefined, camera.generation());
				nextTick(() => {
					transitionTo(MapState.READY);
				});
			});
		} catch (error) {
			clearTimeout(timeoutId);
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
			}
			console.error('Error in handleDragEnd:', error);
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}
	}

	function handleZoomChanged(zoom: number | undefined): void {
		if (!gmapRef.value?.mapInstance || !zoom) return;

		const previousZoom = state.currentZoom;
		state.currentZoom = zoom;
		updateClusterSizes();

		if (!state.isProgrammaticOperation) {
			// If current zoom is less than 16, always clear selections
			if (
				zoom < 16 &&
				selectedClustersData.value.size > 0 &&
				filters.value.h3Ids &&
				filters.value.h3Ids.length > 0
			) {
				clusters.value = [];
				(selectedClustersData.value as Set<string>).clear();

				const newFiltersData = {
					...filters.value,
					h3Ids: [],
					bounds: filters.value.bounds,
					zoom: zoom
				} as PropertyFilters;

				void mapMode.resetSelection(newFiltersData, forceUpdateClusters);
				return;
			}

			// For zooms 16+, keep context logic
			const wasInHighZoomContext = previousZoom >= 16;
			const isInHighZoomContext = zoom >= 16;

			const shouldClearSelection =
				wasInHighZoomContext !== isInHighZoomContext &&
				selectedClustersData.value.size > 0 &&
				filters.value.h3Ids &&
				filters.value.h3Ids.length > 0;

			if (shouldClearSelection) {
				clusters.value = [];
				(selectedClustersData.value as Set<string>).clear();

				const newFiltersData = {
					...filters.value,
					h3Ids: [],
					bounds: filters.value.bounds,
					zoom: zoom
				} as PropertyFilters;

				void mapMode.resetSelection(newFiltersData, forceUpdateClusters);
				return;
			}

			// Clear visual clusters when zoom decreases, except between 16-17
			if (
				zoom < previousZoom &&
				!(wasInHighZoomContext && isInHighZoomContext)
			) {
				clusters.value = [];
			}
		}

		if (
			isInitializing.value ||
			!isReady.value ||
			state.isProgrammaticOperation
		) {
			return;
		}

		transitionTo(MapState.UPDATING_FROM_INTERACTION);

		let idleListener: google.maps.MapsEventListener | null = null;

		const timeoutId = setTimeout(() => {
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
				idleListener = null;
			}
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}, IDLE_TIMEOUT_MS);

		try {
			if (!gmapRef.value?.mapInstance) {
				clearTimeout(timeoutId);
				nextTick(() => {
					transitionTo(MapState.READY);
				});
				return;
			}

			idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
				clearTimeout(timeoutId);
				if (idleListener) {
					google.maps.event.removeListener(idleListener);
					idleListener = null;
				}

				const bounds = gmapRef.value?.mapInstance?.getBounds();
				if (!bounds) {
					nextTick(() => {
						transitionTo(MapState.READY);
					});
					return;
				}

				debouncedUpdateMapBounds(bounds, undefined, camera.generation());
				nextTick(() => {
					transitionTo(MapState.READY);
				});
			});
		} catch (error) {
			clearTimeout(timeoutId);
			if (idleListener) {
				google.maps.event.removeListener(idleListener);
			}
			console.error('Error in handleZoomChanged:', error);
			nextTick(() => {
				transitionTo(MapState.READY);
			});
		}
	}

	function handleAutoZoom(zoomLevel: number): void {
		if (!gmapRef.value?.mapInstance || preserveCameraOnClusterUpdate) return;

		markProgrammaticCamera();
		gmapRef.value.mapInstance.setZoom(zoomLevel);
		state.currentZoom = zoomLevel;
	}

	function handleClusterNavigation(): void {
		transitionTo(MapState.NAVIGATING_BY_CLUSTER);

		nextTick(() => {
			transitionTo(MapState.READY);
		});
	}

	function initializeMapFromUrlBounds(): void {
		applyUrlBoundsToMap();
	}

	function initializeMapWithFitBounds(): void {
		transitionTo(MapState.APPLYING_FIT_BOUNDS);
	}

	async function performInitialMapLoad(): Promise<void> {
		const revision = locationRevision;
		const initialFilters = pendingLocationFilters ?? filters.value;

		try {
			const hasUrlBounds = applyUrlBoundsToMap(initialFilters);
			if (!hasUrlBounds && !config.pageConfig.wishlistId) {
				await initializeMapFromCurrentFilters(true, initialFilters);
			}
			if (disposed || revision !== locationRevision) return;

			await loadClusters(
				config.pageConfig.wishlistId
					? {
							...initialFilters,
							wishlistId: config.pageConfig.wishlistId
						}
					: initialFilters
			);
			if (disposed || revision !== locationRevision) return;

			if (preserveCameraOnClusterUpdate || clusters.value.length === 0) {
				transitionTo(MapState.READY);
				updateClusterSizes();
				return;
			}

			initializeMapWithFitBounds();
			fitBoundsToMarkers();
		} catch (error) {
			console.error('Error during initial map load:', error);
			if (!disposed && revision === locationRevision) {
				transitionTo(MapState.READY);
			}
		}
	}

	let initialMapLoadPromise: Promise<void> | null = null;

	function handleInitialMapLoad(): Promise<void> {
		if (!gmapRef.value?.mapInstance) return Promise.resolve();
		initialMapLoadPromise ??= performInitialMapLoad();
		return initialMapLoadPromise;
	}

	async function processPoiLocationChange(
		lat: number,
		lng: number
	): Promise<void> {
		const revision = locationRevision;
		await fitMapFromPoi(lat, lng);
		if (!disposed && revision === locationRevision) finishLocationProcessing();
	}

	async function processLocationChange(
		newData: LocationChangeData
	): Promise<void> {
		if (preserveCameraOnClusterUpdate) return;
		const { lat, lng } = newData;

		const poi = mapMode.toPoi(lat, lng);
		if (poi) {
			await processPoiLocationChange(poi.lat, poi.lng);
			return;
		}
	}

	watch(
		() => ({
			lat: filters.value.poiLocationLat,
			lng: filters.value.poiLocationLng,
			regions: filters.value.regions,
			cities: filters.value.cities,
			states: filters.value.states,
			macroregions: filters.value.macroregions
		}),
		async (newLocationData, oldLocationData) => {
			if (!isUpdatingLocation.value || !oldLocationData) return;
			if (!hasSignificantLocationChange(newLocationData, oldLocationData))
				return;

			await processLocationChange(newLocationData);
		},
		{ immediate: false }
	);

	function getBoundsString(bounds: google.maps.LatLngBounds): string {
		const { north, south, east, west } = bounds.toJSON();
		return `${west},${south},${east},${north}`;
	}

	function updateMapClusters(
		bounds?: google.maps.LatLngBounds,
		updatedFilters?: PropertyFilters
	): PropertyFilters {
		const newFilters = updatedFilters || { ...filters.value };
		const boundsString = bounds ? getBoundsString(bounds) : undefined;
		const currentZoom = bounds
			? gmapRef.value?.mapInstance?.getZoom()
			: undefined;
		const hasSelectedClusters =
			selectedClustersData.value.size > 0 &&
			newFilters.h3Ids &&
			newFilters.h3Ids.length > 0;

		const clustersFiltersData = {
			...newFilters,
			bounds: boundsString,
			zoom: currentZoom
		} as PropertyFilters;

		mapMode.onViewportComputed(clustersFiltersData, updateClusters);

		return {
			...newFilters,
			bounds: boundsString,
			h3Ids: hasSelectedClusters ? newFilters.h3Ids : [],
			zoom: currentZoom
		} as PropertyFilters;
	}

	async function updateUrlAndListing(
		filtersData: PropertyFilters
	): Promise<void> {
		await mapMode.writeViewport(filtersData, config.pageConfig);
	}

	function updateMapBounds(
		bounds?: google.maps.LatLngBounds,
		updatedFilters?: PropertyFilters,
		origin?: 'control'
	): void {
		if (!mapMode.acceptsViewport(camera, origin)) {
			mapMode.refreshWithoutWrite(updateClusters);
			return;
		}
		const sharedFiltersData = updateMapClusters(bounds, updatedFilters);

		if ((sharedFiltersData as any).skipListingUpdate) {
			return;
		}

		updateUrlAndListing(sharedFiltersData);
	}

	const debouncedUpdateMapBounds = createSafeDebouncedFunction(
		(
			bounds?: google.maps.LatLngBounds,
			updatedFilters?: PropertyFilters,
			generation = camera.generation()
		) => {
			if (!camera.isCurrent(generation)) return;
			updateMapBounds(bounds, updatedFilters);
		},
		DEBOUNCE_DELAYS.MAP_BOUNDS,
		{
			maxWait: DEBOUNCE_DELAYS.MAX_WAIT
		}
	);

	const updateClusters = (filtersData: PropertyFilters) => {
		if (!canUpdateClusters()) {
			state.pendingOperations.clustersUpdate = filtersData;
			return;
		}
		void loadClusters(filtersData);
	};

	const forceUpdateClusters = (filtersData: PropertyFilters) => {
		void loadClusters(filtersData);
	};

	const clusterStateHandlers = {
		[MapState.APPLYING_FIT_BOUNDS]: (clusters: Cluster[]) => {
			if (clusters?.length > 0) {
				fitBoundsToMarkers();
			}
		},
		[MapState.UPDATING_LOCATION]: (clusters: Cluster[]) => {
			if (preserveCameraOnClusterUpdate) {
				finishLocationProcessing();
			} else if (clusters?.length > 0) {
				fitBoundsToMarkers();
			} else {
				const revision = locationRevision;
				initializeMapFromCurrentFilters(
					false,
					pendingLocationFilters ?? filters.value
				).then(() => {
					if (!disposed && revision === locationRevision)
						finishLocationProcessing();
				});
			}
		},
		[MapState.UPDATING_SEE_ALL_CITY]: (clusters: Cluster[]) => {
			finishLocationProcessing();
		}
	} as const;

	function handleClusterStateChange(newClusters: Cluster[]): void {
		const handler =
			clusterStateHandlers[
				state.currentState as keyof typeof clusterStateHandlers
			];

		if (handler) {
			handler(newClusters);
		}

		if (state.pendingOperations.clustersUpdate) {
			const pendingUpdate = state.pendingOperations.clustersUpdate;
			state.pendingOperations.clustersUpdate = null;
			updateClusters(pendingUpdate);
		}

		updateClusterSizes();
	}

	watch(
		() => clusters.value,
		newClusters => {
			const stateWhenClustersChanged = state.currentState;
			nextTick(() => {
				if (stateWhenClustersChanged === MapState.INITIALIZING) {
					updateClusterSizes();
					return;
				}
				handleClusterStateChange(newClusters);
			});
		}
	);

	const eventCleanupFunctions: (() => void)[] = [];

	const regionChangedBus = useEventBus<boolean>(
		usePropertySearchEventKey(EventBusKeys.regionChanged)
	);
	const autoZoomBus = useEventBus<number>(
		usePropertySearchEventKey(EventBusKeys.autoZoom)
	);

	eventCleanupFunctions.push(
		onClientEventBus(regionChangedBus, handleRegionChanged),
		onClientEventBus(autoZoomBus, handleAutoZoom)
	);

	function clearProgrammaticFlag(generation = camera.generation()): void {
		if (!camera.isCurrent(generation)) return;
		if (state.isProgrammaticOperation) {
			state.isProgrammaticOperation = false;
		}
	}

	const stopIdleWatch = watch(
		() => gmapRef.value?.mapInstance,
		mapInstance => {
			if (!mapInstance) return;

			mapInstance.addListener('idle', () => {
				const generation = camera.generation();
				setTimeout(() => {
					clearProgrammaticFlag(generation);
				}, 100);
			});

			stopIdleWatch();
		},
		{ immediate: true }
	);

	watch(
		() => gmapRef.value?.mapInstance,
		mapInstance => {
			if (!mapInstance) return;
			void handleInitialMapLoad();
		},
		{ flush: 'post', immediate: true }
	);

	onUnmounted(() => {
		disposed = true;
		locationRevision++;
		camera.reset();
		eventCleanupFunctions.forEach(cleanup => cleanup());
		eventCleanupFunctions.length = 0;
		clusterSizes.value.clear();
	});

	return {
		beginCameraGesture,
		isCameraGesture: camera.isGesture,
		state: {
			isInitializing,
			isReady,
			isUpdatingLocation,
			isUpdatingSeeAllCity,
			isUpdatingFromInteraction,
			isApplyingFitBounds,
			isNavigatingByCluster,
			currentState: readonly(toRef(state, 'currentState')),
			currentZoom: readonly(toRef(state, 'currentZoom'))
		},
		clusterSizes: readonly(clusterSizes),

		initializeMapFromCurrentFilters,
		processLocationFiltersChange,
		processSeeAllCity,
		handleInitialMapLoad,
		initializeMapWithFitBounds,
		initializeMapFromUrlBounds,
		handleDragEnd,
		handleZoomChanged,
		handleRegionChanged,
		handleClusterNavigation,
		fitBoundsToMarkers,
		updateClusterSizes,
		hasLocationFiltersChanged,
		shouldApplyFitBoundsForLocation,
		updateClusters,
		forceUpdateClusters,
		updateMapBounds,
		clearProgrammaticFlag,
		MapState
	};
}
